import { z } from 'zod';
import {
  activeDefinitions,
  customFieldValuesSchema,
  isEmptyCustomFieldValue,
  isIsoDate,
  normalizeLabel,
  type CustomFieldDefinition,
  type CustomFieldValues,
} from '../custom-fields/contract';
import {
  originSchema,
  tagNamesSchema,
  type CustomerOrigin,
  type CustomerType,
  type PersonRef,
  type UnitRef,
} from '../customers/contract';
import type { ScopeCaller } from '../customers/data-scope';
import type { NextTask } from '../tasks/contract';

/**
 * The pipelines and deals contract (docs/specs/005-funis-e-negocios/spec.md, §3 and §6): the
 * shapes the API answers, the zod schemas of what it accepts and the pure rules both sides
 * share — the totals of the lines, the requirements of a stage, rotting, the first open
 * stage. The screens and the mocked API (`src/mocks/deals`) use the same functions.
 */

/* ------------------------------------------------------------------ constants */

export const PIPELINE_PURPOSES = ['sales', 'operations'] as const;
export type PipelinePurpose = (typeof PIPELINE_PURPOSES)[number];

export const STAGE_TYPES = ['open', 'won', 'lost'] as const;
export type StageType = (typeof STAGE_TYPES)[number];
export type DealStatus = StageType;
export const DEAL_STATUSES = STAGE_TYPES;

/** Core fields a stage may require to be entered (custom fields are `cf.<key>`). */
export const STAGE_CORE_REQUIREMENTS = [
  'value',
  'expectedCloseDate',
  'contact',
  'owner',
  'origin',
] as const;
export type StageCoreRequirement = (typeof STAGE_CORE_REQUIREMENTS)[number];
export type StageRequirement = StageCoreRequirement | `cf.${string}`;

export const CUSTOM_REQUIREMENT_PREFIX = 'cf.';

export const VALUE_SOURCES = ['manual', 'items'] as const;
export type ValueSource = (typeof VALUE_SOURCES)[number];

export const DEAL_SORTS = [
  'createdAt',
  '-createdAt',
  'title',
  '-title',
  'value',
  '-value',
  'expectedCloseDate',
  '-expectedCloseDate',
  'stageEnteredAt',
  '-stageEnteredAt',
] as const;
export type DealSort = (typeof DEAL_SORTS)[number];
export const DEFAULT_DEAL_SORT: DealSort = '-createdAt';

export const DEAL_PAGE_SIZE = 25;
export const DEAL_MAX_PAGE_SIZE = 100;
/** Cards a board column brings at once. */
export const BOARD_COLUMN_LIMIT = 15;
/** Deals a bulk action takes at once. */
export const BULK_MAX = 200;
/** "Sem atividade": no change for more than this many days (the screens' default). */
export const INACTIVE_DAYS = 7;

/** `ownerMembershipId=none` lists the deals without an owner. */
export const NO_DEAL_OWNER = 'none';

export const DEAL_LIMITS = {
  titleMin: 2,
  titleMax: 160,
  pipelineNameMin: 2,
  pipelineNameMax: 60,
  stageNameMax: 40,
  stagesMin: 3,
  stagesMax: 20,
  rottingDaysMax: 365,
  checklistMax: 20,
  checklistLabelMax: 80,
  lostReasonMin: 2,
  lostReasonMax: 60,
  itemCategoryMin: 2,
  itemCategoryMax: 60,
  lostNoteMax: 500,
  itemNameMin: 2,
  itemNameMax: 120,
  itemDescriptionMax: 1000,
  externalRefMax: 80,
  imageUrlMax: 500,
  /** An uploaded item image: PNG, JPEG, GIF or WebP up to 5 MB (same rule as the API). */
  imageMaxBytes: 5 * 1024 * 1024,
  lineNameMax: 120,
  linesMax: 50,
  quantityMax: 9999,
  searchMax: 120,
} as const;

/** Codes of the errors of this contract (translated in `errors.api.<CODE>`). */
export const DEAL_ERROR_CODES = [
  'PIPELINE_INVALID',
  'PIPELINE_UNIT_INVALID',
  'PIPELINE_NOT_FOUND',
  'PIPELINE_NAME_TAKEN',
  'PIPELINE_STAGE_IN_USE',
  'PIPELINE_UNIT_CONFLICT',
  'PIPELINE_LAST_ACTIVE',
  'LOST_REASON_INVALID',
  'LOST_REASON_NOT_FOUND',
  'LOST_REASON_LABEL_TAKEN',
  'ITEM_INVALID',
  'ITEM_NOT_FOUND',
  'ITEM_LOCKED',
  'DEAL_INVALID',
  'DEAL_NOT_FOUND',
  'DEAL_CONTACT_INVALID',
  'DEAL_PIPELINE_INVALID',
  'DEAL_STAGE_INVALID',
  'DEAL_STAGE_REQUIREMENTS',
  'DEAL_LOST_REASON_REQUIRED',
  'DEAL_LOST_NOTE_REQUIRED',
  'DEAL_ITEM_INVALID',
  'DEAL_OWNER_INVALID',
  'DEAL_OWNER_OUT_OF_REACH',
  'DEAL_UNIT_INVALID',
  'DEAL_UNIT_OUT_OF_REACH',
  'DEAL_TEAM_INVALID',
  'DEAL_ALREADY_OWNED',
  'DEAL_ARCHIVED',
] as const;
export type DealErrorCode = (typeof DEAL_ERROR_CODES)[number];

/* ------------------------------------------------------------------ shapes */

export interface ChecklistItem {
  id: string;
  label: string;
}

export interface PipelineStage {
  id: string;
  name: string;
  type: StageType;
  order: number;
  /** 0–100; the weight of the stage in the forecast. */
  probability: number | null;
  /** An open deal is "parado" after this many days in the stage. */
  rottingDays: number | null;
  requiredFields: StageRequirement[];
  checklist: ChecklistItem[];
  /** Deals in the stage, archived included (what keeps it from being removed). */
  dealCount: number;
}

export interface Pipeline {
  id: string;
  name: string;
  /** `null` = every unit. */
  unit: UnitRef | null;
  active: boolean;
  /** Whether the pipeline's deals carry items (products and services). */
  itemsEnabled: boolean;
  /** Whether the pipeline's deals carry proposals — only with items. */
  proposalsEnabled: boolean;
  /** Sales, or operations (after-sales, installation): only sales count as revenue. */
  purpose: PipelinePurpose;
  order: number;
  stages: PipelineStage[];
  /** Spec 074, R16: the integration whose sale wins this pipeline's deals. */
  wonLockedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LostReason {
  id: string;
  label: string;
  requiresNote: boolean;
  /** Losing with it drops the opportunity's leads from the pool by default (spec 020). */
  discardsLead: boolean;
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

/** A category of the catalog (QA-04): "Cosméticos", "Imersões", "Clube de assinatura". */
export interface ItemCategory {
  id: string;
  name: string;
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

/** `integration:<id>` (spec 062) and `plugin:<id>`: read-only here, changed at the source. */
export type CatalogItemSource = 'manual' | `plugin:${string}` | `integration:${string}`;

export interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  priceCents: number | null;
  currency: 'BRL';
  externalRef: string | null;
  source: CatalogItemSource;
  /** Spec 062: the code and the kind the integration that manages the item sends. */
  sku: string | null;
  itemKind: string | null;
  /** The external image URL typed in the form (null when an image was uploaded). */
  imageUrl: string | null;
  /** What to show: the uploaded image's route, else the external URL. */
  imageSrc: string | null;
  imageUploaded: boolean;
  active: boolean;
  categoryId: string | null;
  category: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface DealItem {
  id: string;
  /** The catalog item, when the line came from it. */
  itemId: string | null;
  name: string;
  quantity: number;
  unitPriceCents: number;
  /** 0–100, up to two decimals. */
  discountPercent: number;
  totalCents: number;
}

export interface DealTag {
  id: string;
  name: string;
}

/** Who an opportunity is about: the customer, else its contact (spec 034). */
export function dealPersonName(deal: {
  customer: { displayName: string } | null;
  contact: { name: string } | null;
}): string {
  return deal.customer?.displayName ?? deal.contact?.name ?? '';
}

/** The contact of a deal with the ways to reach them, in their order (phones are digits). */
export interface DealContact {
  id: string;
  name: string;
  phones: string[];
  emails: string[];
}

export interface DealSummary {
  id: string;
  title: string;
  /** Null while the opportunity is about a contact alone, before the sale (spec 034). */
  customer: { id: string; displayName: string; type: CustomerType } | null;
  contact: DealContact | null;
  pipelineId: string;
  stageId: string;
  status: DealStatus;
  owner: PersonRef | null;
  unit: UnitRef;
  team: { id: string; name: string } | null;
  valueCents: number;
  /** `YYYY-MM-DD`. */
  expectedCloseDate: string | null;
  tags: DealTag[];
  stageEnteredAt: string;
  daysInStage: number;
  rotting: boolean;
  wonAt: string | null;
  lostAt: string | null;
  lostReason: { id: string; label: string } | null;
  lastActivityAt: string;
  /** The nearest open task (docs/specs/006, rule 17); `null` = "Sem próxima atividade". */
  nextTask: NextTask | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Deal extends DealSummary {
  valueSource: ValueSource;
  items: DealItem[];
  origin: CustomerOrigin;
  customFields: CustomFieldValues;
  checklistDone: string[];
  lostNote: string | null;
  /** The open stage a closed deal left: where "Reabrir" goes by default. */
  closedFromStageId: string | null;
}

export interface DealList {
  items: DealSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DealBoardColumn {
  stageId: string;
  count: number;
  totalCents: number;
  /** The total weighted by the stage's probability (the simple forecast). */
  weightedCents: number;
  items: DealSummary[];
  hasMore: boolean;
}

export interface DealBoard {
  pipelineId: string;
  columns: DealBoardColumn[];
}

export interface DealsSummary {
  openCount: number;
  openValueCents: number;
  /** Won in the last 30 days. */
  wonCount: number;
  wonValueCents: number;
  rottingCount: number;
  /** Up to five open deals with a forecast, the nearest first. */
  upcoming: DealSummary[];
}

export interface DealTagWithUsage extends DealTag {
  usageCount: number;
}

export interface BulkDealResult {
  updated: string[];
  failed: { id: string; code: string }[];
}

/* ------------------------------------------------------------------ schemas */

const isoDate = z.string().refine(isIsoDate, 'invalid_date');
const cents = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const optionalText = (max: number) => z.string().trim().max(max).nullable();

const requirementSchema = z.custom<StageRequirement>(
  (value) =>
    typeof value === 'string' &&
    ((STAGE_CORE_REQUIREMENTS as readonly string[]).includes(value) ||
      /^cf\.[a-z][a-z0-9_]*$/.test(value)),
  'invalid_requirement',
);

const checklistItemInput = z
  .object({
    id: z.string().min(1).max(40).optional(),
    label: z.string().trim().min(1).max(DEAL_LIMITS.checklistLabelMax),
  })
  .strict();

export const stageInputSchema = z
  .object({
    /** Present on a stage that already exists. */
    id: z.string().min(1).optional(),
    name: z.string().trim().min(1).max(DEAL_LIMITS.stageNameMax),
    type: z.enum(STAGE_TYPES),
    probability: z.number().int().min(0).max(100).nullable().optional(),
    rottingDays: z.number().int().min(1).max(DEAL_LIMITS.rottingDaysMax).nullable().optional(),
    requiredFields: z.array(requirementSchema).max(20).optional(),
    checklist: z.array(checklistItemInput).max(DEAL_LIMITS.checklistMax).optional(),
  })
  .strict();

export type StageInput = z.input<typeof stageInputSchema>;

export type PipelineIssue =
  | 'stages_count'
  | 'open_missing'
  | 'won_count'
  | 'lost_count'
  | 'closed_last'
  | 'duplicate_name'
  | 'rotting_on_closed';

/**
 * What breaks the invariants of a pipeline (spec rule 3): at least one open stage, exactly one
 * won and one lost, the closed ones after the open ones (won, then lost), 3–20 stages with
 * distinct names, rotting only on open stages. Empty when the stages are fine.
 */
export function pipelineIssues(
  stages: readonly Pick<StageInput, 'name' | 'type' | 'rottingDays'>[],
): PipelineIssue[] {
  const issues: PipelineIssue[] = [];
  if (stages.length < DEAL_LIMITS.stagesMin || stages.length > DEAL_LIMITS.stagesMax) {
    issues.push('stages_count');
  }
  const types = stages.map((stage) => stage.type);
  if (!types.includes('open')) issues.push('open_missing');
  if (types.filter((type) => type === 'won').length !== 1) issues.push('won_count');
  if (types.filter((type) => type === 'lost').length !== 1) issues.push('lost_count');
  const firstClosed = types.findIndex((type) => type !== 'open');
  const lastOpen = types.lastIndexOf('open');
  const wonAt = types.indexOf('won');
  const lostAt = types.indexOf('lost');
  if ((firstClosed !== -1 && lastOpen > firstClosed) || (wonAt !== -1 && lostAt < wonAt)) {
    issues.push('closed_last');
  }
  const names = stages.map((stage) => normalizeLabel(stage.name));
  if (new Set(names).size !== names.length) issues.push('duplicate_name');
  if (stages.some((stage) => stage.type !== 'open' && stage.rottingDays)) {
    issues.push('rotting_on_closed');
  }
  return issues;
}

const pipelineName = z
  .string()
  .trim()
  .min(DEAL_LIMITS.pipelineNameMin)
  .max(DEAL_LIMITS.pipelineNameMax);

export const createPipelineSchema = z
  .object({
    name: pipelineName,
    unitId: z.string().min(1).nullable().optional(),
    active: z.boolean().optional(),
    itemsEnabled: z.boolean().optional(),
    proposalsEnabled: z.boolean().optional(),
    purpose: z.enum(PIPELINE_PURPOSES).optional(),
    stages: z.array(stageInputSchema),
  })
  .strict();

export type CreatePipelineInput = z.input<typeof createPipelineSchema>;

export const updatePipelineSchema = z
  .object({
    name: pipelineName.optional(),
    unitId: z.string().min(1).nullable().optional(),
    active: z.boolean().optional(),
    itemsEnabled: z.boolean().optional(),
    proposalsEnabled: z.boolean().optional(),
    purpose: z.enum(PIPELINE_PURPOSES).optional(),
    stages: z.array(stageInputSchema).optional(),
  })
  .strict();

export type UpdatePipelineInput = z.input<typeof updatePipelineSchema>;

export const reorderSchema = z.object({ ids: z.array(z.string().min(1)).min(1) }).strict();

export const lostReasonInputSchema = z
  .object({
    label: z.string().trim().min(DEAL_LIMITS.lostReasonMin).max(DEAL_LIMITS.lostReasonMax),
    requiresNote: z.boolean().optional(),
    discardsLead: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .strict();

export type LostReasonInput = z.input<typeof lostReasonInputSchema>;
export const updateLostReasonSchema = lostReasonInputSchema.partial().strict();
export type UpdateLostReasonInput = z.input<typeof updateLostReasonSchema>;

export const itemCategoryInputSchema = z
  .object({
    name: z.string().trim().min(DEAL_LIMITS.itemCategoryMin).max(DEAL_LIMITS.itemCategoryMax),
    active: z.boolean().optional(),
  })
  .strict();
export type ItemCategoryInput = z.input<typeof itemCategoryInputSchema>;
export const updateItemCategorySchema = itemCategoryInputSchema.partial().strict();
export type UpdateItemCategoryInput = z.input<typeof updateItemCategorySchema>;

const httpUrl = z
  .string()
  .trim()
  .max(DEAL_LIMITS.imageUrlMax)
  .refine((value) => /^https?:\/\/\S+$/i.test(value), 'invalid_url');

export const catalogItemInputSchema = z
  .object({
    name: z.string().trim().min(DEAL_LIMITS.itemNameMin).max(DEAL_LIMITS.itemNameMax),
    description: optionalText(DEAL_LIMITS.itemDescriptionMax).optional(),
    priceCents: cents.nullable().optional(),
    externalRef: optionalText(DEAL_LIMITS.externalRefMax).optional(),
    imageUrl: httpUrl.nullable().optional(),
    active: z.boolean().optional(),
    categoryId: z.string().min(1).nullable().optional(),
  })
  .strict();

export type CatalogItemInput = z.input<typeof catalogItemInputSchema>;
export const updateCatalogItemSchema = catalogItemInputSchema.partial().strict();
export type UpdateCatalogItemInput = z.input<typeof updateCatalogItemSchema>;

export const dealItemInputSchema = z
  .object({
    itemId: z.string().min(1).nullable().optional(),
    name: z.string().trim().min(1).max(DEAL_LIMITS.lineNameMax),
    quantity: z.number().int().min(1).max(DEAL_LIMITS.quantityMax),
    unitPriceCents: cents,
    discountPercent: z
      .number()
      .min(0)
      .max(100)
      .refine((value) => Math.round(value * 100) === value * 100, 'two_decimals')
      .optional(),
  })
  .strict();

export type DealItemInput = z.input<typeof dealItemInputSchema>;

const dealFields = {
  title: z.string().trim().min(DEAL_LIMITS.titleMin).max(DEAL_LIMITS.titleMax).optional(),
  contactId: z.string().min(1).nullable().optional(),
  ownerMembershipId: z.string().min(1).nullable().optional(),
  unitId: z.string().min(1).optional(),
  teamId: z.string().min(1).nullable().optional(),
  valueSource: z.enum(VALUE_SOURCES).optional(),
  valueCents: cents.optional(),
  items: z.array(dealItemInputSchema).max(DEAL_LIMITS.linesMax).optional(),
  expectedCloseDate: isoDate.nullable().optional(),
  origin: originSchema.optional(),
  tags: tagNamesSchema.optional(),
  customFields: customFieldValuesSchema.optional(),
};

/**
 * Someone not registered yet (spec 071): the API finds the contact by the phone or the e-mail,
 * or makes it with the opportunity. National digits for the phone.
 */
export const newDealContactSchema = z
  .object({
    name: z.string().trim().min(2).max(160),
    phone: z
      .string()
      .regex(/^\d{10,11}$/)
      .optional(),
    email: z.email().max(254).optional(),
    /** The company the person speaks for (a shared contact card's). */
    company: z.string().trim().min(1).max(160).optional(),
    unitId: z.string().min(1).optional(),
  })
  .strict();

/**
 * An opportunity is about a person (`contactId`, or a `newContact` made with it) and,
 * optionally, about an account (`customerId`, spec 034): one of them is required, as the API
 * says.
 */
export const createDealSchema = z
  .object({
    customerId: z.string().min(1).optional(),
    pipelineId: z.string().min(1).optional(),
    stageId: z.string().min(1).optional(),
    newContact: newDealContactSchema.optional(),
    ...dealFields,
  })
  .strict()
  .refine(
    (deal) =>
      deal.newContact !== undefined ||
      deal.customerId !== undefined ||
      typeof deal.contactId === 'string',
    {
      path: ['contactId'],
      message: 'A deal needs a customer, a contact or a new contact',
    },
  );

export type CreateDealInput = z.input<typeof createDealSchema>;

export const updateDealSchema = z
  .object({
    ...dealFields,
    /** Links the customer of an opportunity that has none (spec 034). */
    customerId: z.string().min(1).optional(),
    checklistDone: z.array(z.string().min(1)).max(200).optional(),
  })
  .strict();

export type UpdateDealInput = z.input<typeof updateDealSchema>;

/** Values a move may bring for the requirements of the target stage (rule 16). */
export const moveFieldsSchema = z
  .object({
    valueCents: cents.optional(),
    expectedCloseDate: isoDate.nullable().optional(),
    contactId: z.string().min(1).nullable().optional(),
    ownerMembershipId: z.string().min(1).nullable().optional(),
    origin: originSchema.optional(),
    customFields: customFieldValuesSchema.optional(),
  })
  .strict();

export type MoveFields = z.input<typeof moveFieldsSchema>;

export const moveDealSchema = z
  .object({
    stageId: z.string().min(1),
    fields: moveFieldsSchema.optional(),
    lostReasonId: z.string().min(1).optional(),
    lostNote: z.string().trim().max(DEAL_LIMITS.lostNoteMax).optional(),
    /** Drop the opportunity's leads from the pool (`true`) or send them back (`false`). */
    discardLeads: z.boolean().optional(),
    /** A loss also cancels the opportunity's open tasks. */
    cancelOpenTasks: z.boolean().optional(),
  })
  .strict();

export type MoveDealInput = z.input<typeof moveDealSchema>;

const bulkIds = z.array(z.string().min(1)).min(1).max(BULK_MAX);

export const bulkDealSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('assign'),
      ids: bulkIds,
      ownerMembershipId: z.string().min(1).nullable(),
    })
    .strict(),
  z.object({ action: z.literal('move'), ids: bulkIds, stageId: z.string().min(1) }).strict(),
  z.object({ action: z.literal('archive'), ids: bulkIds }).strict(),
]);

export type BulkDealInput = z.input<typeof bulkDealSchema>;

/** The query of `GET /deals` (and of `GET /deals/board`, minus stage, status, page and sort). */
export const dealListQuerySchema = z.object({
  q: z.string().trim().max(DEAL_LIMITS.searchMax).optional(),
  pipelineId: z.string().min(1).optional(),
  stageId: z.string().min(1).optional(),
  status: z.enum(DEAL_STATUSES).optional(),
  ownerMembershipId: z.string().min(1).optional(),
  unitId: z.string().min(1).optional(),
  teamId: z.string().min(1).optional(),
  tag: z.string().min(1).optional(),
  itemCategoryId: z.string().min(1).optional(),
  customerId: z.string().min(1).optional(),
  contactId: z.string().min(1).optional(),
  valueMin: z.coerce.number().int().min(0).optional(),
  valueMax: z.coerce.number().int().min(0).optional(),
  closeFrom: isoDate.optional(),
  closeTo: isoDate.optional(),
  closeDate: z.enum(['none']).optional(),
  closedFrom: isoDate.optional(),
  closedTo: isoDate.optional(),
  rotting: z.enum(['true', 'false']).optional(),
  leads: z.enum(['true', 'false']).optional(),
  inactiveDays: z.coerce.number().int().min(1).max(3650).optional(),
  nextTask: z.enum(['none']).optional(),
  archived: z.enum(['true', 'false', 'all']).optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(DEAL_MAX_PAGE_SIZE).optional(),
  sort: z.enum(DEAL_SORTS).optional(),
});

export interface DealListQuery {
  q?: string;
  pipelineId?: string;
  stageId?: string;
  status?: DealStatus;
  /** A membership id or `none`. */
  ownerMembershipId?: string;
  unitId?: string;
  teamId?: string;
  tag?: string;
  /** Deals with a line of a catalog item of this category (QA-04). */
  itemCategoryId?: string;
  customerId?: string;
  /** The opportunities of one person (spec 064). */
  contactId?: string;
  valueMin?: number;
  valueMax?: number;
  closeFrom?: string;
  closeTo?: string;
  closeDate?: 'none';
  /** Won and lost deals closed in these days; open deals whatever their dates. */
  closedFrom?: string;
  closedTo?: string;
  rotting?: boolean;
  leads?: boolean;
  inactiveDays?: number;
  /** `none` = without an open task (docs/specs/006, rule 17). */
  nextTask?: 'none';
  /** `true` = only archived; `all` = active + archived (customer fiche). */
  archived?: boolean | 'all';
  page?: number;
  pageSize?: number;
  sort?: DealSort;
}

export type DealBoardQuery = Omit<
  DealListQuery,
  'stageId' | 'status' | 'page' | 'pageSize' | 'sort'
> & {
  pipelineId: string;
};

/* ------------------------------------------------------------------ rules */

/** The total of a line: quantity × price, less the discount, in whole cents (rule 14). */
export function lineTotalCents(line: {
  quantity: number;
  unitPriceCents: number;
  discountPercent?: number | undefined;
}): number {
  const discount = line.discountPercent ?? 0;
  return Math.round((line.quantity * line.unitPriceCents * (100 - discount)) / 100);
}

export function itemsTotalCents(
  lines: readonly { quantity: number; unitPriceCents: number; discountPercent?: number }[],
): number {
  return lines.reduce((sum, line) => sum + lineTotalCents(line), 0);
}

/** The stages in pipeline order. */
export function orderedStages<T extends { order: number }>(stages: readonly T[]): T[] {
  return stages.toSorted((a, b) => a.order - b.order);
}

/** The first open stage: where a deal is born and where it is a lead (rule 24). */
export function firstOpenStage<T extends { type: StageType; order: number }>(
  stages: readonly T[],
): T | undefined {
  return orderedStages(stages).find((stage) => stage.type === 'open');
}

/** The open stage after `stageId` ("Qualificar"), if there is one. */
export function nextOpenStage<T extends { id: string; type: StageType; order: number }>(
  stages: readonly T[],
  stageId: string,
): T | undefined {
  const open = orderedStages(stages).filter((stage) => stage.type === 'open');
  const index = open.findIndex((stage) => stage.id === stageId);
  return index === -1 ? undefined : open[index + 1];
}

/** Whole days between an instant and `now`. */
export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000));
}

/** An open deal past the stage's rotting days (rule 18). */
export function isRotting(
  deal: { status: DealStatus; stageEnteredAt: string },
  stage: { rottingDays: number | null } | undefined,
  now: Date = new Date(),
): boolean {
  if (deal.status !== 'open' || !stage?.rottingDays) return false;
  return daysSince(deal.stageEnteredAt, now) > stage.rottingDays;
}

/** What a deal has, as the requirements of a stage read it. */
export interface RequirementSubject {
  valueCents: number;
  expectedCloseDate: string | null;
  contactId: string | null;
  ownerMembershipId: string | null;
  originChannel: string | null;
  customFields: CustomFieldValues;
}

/**
 * The requirements of `stage` that `subject` does not meet (rule 16). A custom field
 * requirement only counts while the field exists and is active.
 */
export function missingRequirements(
  stage: { requiredFields: readonly StageRequirement[] },
  subject: RequirementSubject,
  definitions: readonly CustomFieldDefinition[],
): StageRequirement[] {
  return stage.requiredFields.filter((requirement) => {
    switch (requirement) {
      case 'value':
        return subject.valueCents <= 0;
      case 'expectedCloseDate':
        return !subject.expectedCloseDate;
      case 'contact':
        return !subject.contactId;
      case 'owner':
        return !subject.ownerMembershipId;
      case 'origin':
        return !subject.originChannel;
      default: {
        const key = requirement.slice(CUSTOM_REQUIREMENT_PREFIX.length);
        const definition = definitions.find(
          (candidate) => candidate.key === key && candidate.entity === 'deal',
        );
        if (!definition?.active) return false;
        return isEmptyCustomFieldValue(subject.customFields[key]);
      }
    }
  });
}

/** A deal as the requirements read it. */
export function requirementSubject(
  deal: DealSummary & Partial<Pick<Deal, 'origin' | 'customFields'>>,
): RequirementSubject {
  return {
    valueCents: deal.valueCents,
    expectedCloseDate: deal.expectedCloseDate,
    contactId: deal.contact?.id ?? null,
    ownerMembershipId: deal.owner?.membershipId ?? null,
    originChannel: deal.origin?.channel ?? null,
    customFields: deal.customFields ?? {},
  };
}

/**
 * The pipelines a caller may put deals in: active, and of every unit or one of theirs (all
 * of them in the organization scope). The board offers only these.
 */
export function pipelinesForCaller<T extends { active: boolean; unit: UnitRef | null }>(
  pipelines: readonly T[],
  caller: Pick<ScopeCaller, 'dataScope' | 'unitIds'> | null,
): T[] {
  return pipelines.filter(
    (pipeline) =>
      pipeline.active &&
      (pipeline.unit === null ||
        caller === null ||
        caller.dataScope === 'organization' ||
        caller.unitIds.includes(pipeline.unit.id)),
  );
}

/** The image types an item accepts on upload (the API checks the bytes again). */
export const ITEM_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

/**
 * The custom fields the form asks: the active required fields of deals and the ones the
 * stage requires — both as required — among the fields of the chosen pipeline (QA-24).
 */
export function customFieldsFor(
  definitions: readonly CustomFieldDefinition[] | undefined,
  requirements: readonly string[],
  pipelineId: string | undefined,
): CustomFieldDefinition[] {
  const asked: CustomFieldDefinition[] = [];
  for (const definition of activeDefinitions(definitions, pipelineId)) {
    const required =
      definition.required || requirements.includes(`${CUSTOM_REQUIREMENT_PREFIX}${definition.key}`);
    if (required) asked.push({ ...definition, required: true });
  }
  return asked;
}
